import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Lock, KeyRound } from "lucide-react";
import { adminLogin } from "../lib/api";
import { setAdminToken } from "../lib/adminAuth";

export default function AdminLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await adminLogin(email.trim(), password);
      setAdminToken(res.token);
      toast.success("Signed in", { description: res.admin.email });
      nav("/cursedgold45", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status !== 429) {
        toast.error("Sign-in failed", { description: String(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
          },
        }}
      />
      <header className="border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">Admin · sign-in</div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="h-12 w-12 rounded-md bg-violet-50 border border-violet-200 grid place-items-center mx-auto mb-4">
              <Lock className="h-5 w-5 text-violet-700" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              Owner-only console
            </h1>
            <p className="text-slate-600 text-sm mt-2">
              Single-user admin gate. 12-hour session.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="panel p-6 space-y-4"
            data-testid="admin-login-form"
          >
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                data-testid="admin-email"
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
                placeholder="admin@cve-analytics.io"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-btn"
              className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-medium px-4 py-2.5 rounded-md transition-colors disabled:opacity-50"
            >
              <KeyRound className="h-4 w-4" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-[11px] text-slate-500 font-mono text-center">
              Brute-force protected · 3 attempts / minute / IP
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
